import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';
import { OUTFITS } from '../data/outfits';
import { OUTFITS_BY_ID } from '../data/outfits';
import { GameState } from '../systems/GameState';
import { EconomyEngine } from '../systems/EconomyEngine';
import { OutfitIconRenderer } from '../systems/OutfitIconRenderer';
import { svgStringToTexture } from '../utils/svgToTexture';
import { Button } from '../ui/Button';
import { ToastManager } from '../ui/ToastManager';
import { AudioManager } from '../systems/AudioManager';
import type { HairStyle, OutfitSlot } from '../types';

const RARITY_COLOR: Record<string, number> = {
  common: 0xcccccc,
  rare:   0x6fd4c4,
  epic:   0x9b6bff,
  legend: 0xffc94c,
  season: 0xff6fa1,
};

const RARITY_LABEL: Record<string, string> = {
  common: 'COMMON',
  rare:   'RARE',
  epic:   'EPIC',
  legend: 'LEGEND',
  season: 'SEASON',
};

const RARITY_TEXT_COLOR: Record<string, string> = {
  common: '#888888',
  rare:   '#2a8b7a',
  epic:   '#6a3acf',
  legend: '#c89020',
  season: '#c0335a',
};

type CategoryDef = { id: OutfitSlot | 'all'; label: string; emoji: string };
const CATEGORIES: CategoryDef[] = [
  { id: 'all',     label: '전체',   emoji: '🛒' },
  { id: 'top',     label: '상의',   emoji: '👕' },
  { id: 'bottom',  label: '하의',   emoji: '👖' },
  { id: 'hair',    label: '헤어',   emoji: '💇' },
  { id: 'hairpin', label: '머리삔', emoji: '🎀' },
  { id: 'glasses', label: '안경',   emoji: '🕶️' },
  { id: 'bag',     label: '가방',   emoji: '🛍️' },
  { id: 'shoes',   label: '신발',   emoji: '👟' },
];

// Grid layout
const CARD_W = 180;
const CARD_H = 220;
const COLS = 4;
const COL_GAP = 220;
const START_X = 300;
const GRID_TOP = 180;  // 카테고리 탭 자리 확보
const GRID_BOTTOM = GAME_HEIGHT - 90;
const VISIBLE_HEIGHT = GRID_BOTTOM - GRID_TOP;

// Preview panel (right side)
const PREVIEW_X = GAME_WIDTH - 160;
const PREVIEW_Y = GAME_HEIGHT / 2 - 20;

export class ShopScene extends Phaser.Scene {
  private toast!: ToastManager;
  private gridContainer: Phaser.GameObjects.Container | null = null;
  private tabContainer: Phaser.GameObjects.Container | null = null;
  private currentCategory: CategoryDef['id'] = 'all';
  private countLabel: Phaser.GameObjects.Text | null = null;

  // 카드별 elements 추적 (구매 시 부분 업데이트용)
  private cardElements: Map<string, {
    icon?: Phaser.GameObjects.Image;
    cost?: Phaser.GameObjects.Text;
    buyBtn?: Phaser.GameObjects.Container;
    card?: Phaser.GameObjects.Graphics;
    cardX: number; cardY: number;
  }> = new Map();

  // Preview — DOM <img>를 직접 사용 (Phaser texture cache 우회 → 메모리 안 먹음 → freeze 방지)
  private previewDom: Phaser.GameObjects.DOMElement | null = null;
  private previewSprite: Phaser.GameObjects.Image | null = null;
  private previewBg: Phaser.GameObjects.Graphics | null = null;
  private previewNameLabel: Phaser.GameObjects.Text | null = null;

  // 화폐 표시 (ShopScene 자체에 추가)
  private currencyChips: Phaser.GameObjects.Container | null = null;

  // Scroll — target/current 분리
  private scrollY = 0;
  private targetScrollY = 0;
  private maxScrollY = 0;
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScroll = 0;
  private dragMoved = false;  // 일정 픽셀 이상 움직여야 진짜 drag — tap 오인식 방지
  private dragLastY = 0;
  private dragLastTime = 0;
  private dragVelocity = 0;
  private scrollbarTrack: Phaser.GameObjects.Rectangle | null = null;
  private scrollbarThumb: Phaser.GameObjects.Rectangle | null = null;


  constructor() { super('ShopScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('shop');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#fef7e6');
    if (this.textures.exists('bg_shop')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_shop');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfef7e6, 0.55).setDepth(-99);
    }
    this.toast = new ToastManager(this);
    this.scrollY = 0;
    this.targetScrollY = 0;

    this.add.text(GAME_WIDTH / 2, 72, '🛒 상점', {
      fontFamily: Theme.font.family,
      fontSize: '34px',
      fontStyle: '800',
      color: Theme.hex.text,
    }).setOrigin(0.5);

    // 화폐 칩 (HUD가 가려져도 항상 보이게 ShopScene 자체에 표시)
    this._renderCurrencyChips();

    new Button(this, 100, GAME_HEIGHT - 50, '← 홈', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HUDScene');
        this.scene.start('HomeScene');
      });
    }, { width: 120, height: 48, style: 'secondary' });

    // 카테고리 탭
    this._renderCategoryTabs();

    // Preview panel on right
    this._buildPreviewPanel();

    void this.renderShopGrid();
    this._setupScrollInput();
  }

  // ── Currency Chips (always visible in shop) ───────────────────────────────

  private _renderCurrencyChips(): void {
    if (this.currencyChips) this.currencyChips.destroy();
    this.currencyChips = this.add.container(0, 0).setDepth(50);

    const c = GameState.data.currency;
    const chipsData = [
      { emoji: '⭐', value: c.stars, color: 0xfff5f8, textColor: '#c89020' },
      { emoji: '💰', value: c.coins, color: 0xfff8e0, textColor: '#7a5b00' },
      { emoji: '💎', value: c.gems,  color: 0xf0eaff, textColor: '#6a3acf' },
    ];

    let x = 24;
    const y = 32;
    chipsData.forEach(chip => {
      const text = `${chip.emoji} ${chip.value.toLocaleString()}`;
      // 임시 텍스트로 width 측정
      const tmp = this.add.text(0, 0, text, { fontFamily: Theme.font.family, fontSize: '18px', fontStyle: '800' });
      const w = tmp.width + 32;
      tmp.destroy();

      const bg = this.add.graphics();
      bg.fillStyle(chip.color, 1);
      bg.fillRoundedRect(x, y - 22, w, 44, 22);
      bg.lineStyle(2, 0xff6fa1, 0.4);
      bg.strokeRoundedRect(x, y - 22, w, 44, 22);

      const txt = this.add.text(x + 16, y, text, {
        fontFamily: Theme.font.family, fontSize: '18px', fontStyle: '800',
        color: chip.textColor,
      }).setOrigin(0, 0.5);

      this.currencyChips!.add([bg, txt]);
      x += w + 8;
    });
  }

  // ── Category Tabs ─────────────────────────────────────────────────────────

  private _renderCategoryTabs(): void {
    if (this.tabContainer) this.tabContainer.destroy();
    this.tabContainer = this.add.container(0, 0);

    const tabY = 110;
    const tabW = 120;
    const tabH = 50;
    const startX = 60;
    CATEGORIES.forEach((cat, i) => {
      const x = startX + i * (tabW + 8);
      const active = cat.id === this.currentCategory;

      const bg = this.add.graphics();
      bg.fillStyle(active ? 0xff6fa1 : 0xfff5f8, 1);
      bg.fillRoundedRect(x, tabY - tabH / 2, tabW, tabH, 12);
      bg.lineStyle(2, active ? 0xe54e87 : 0xffd6e6, 1);
      bg.strokeRoundedRect(x, tabY - tabH / 2, tabW, tabH, 12);

      const label = this.add.text(x + tabW / 2, tabY, `${cat.emoji} ${cat.label}`, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '13px',
        fontStyle: '800',
        color: active ? '#ffffff' : '#7a5b80',
      }).setOrigin(0.5);

      const hit = this.add.rectangle(x + tabW / 2, tabY, tabW, tabH, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        if (this.currentCategory === cat.id) return;
        this.currentCategory = cat.id;
        AudioManager.playSfx('click');
        this.scrollY = 0;
    this.targetScrollY = 0;
        this._renderCategoryTabs();
        void this.renderShopGrid();
      });

      this.tabContainer!.add([bg, label, hit]);
    });

    // 카운트 표시 (탭 아래로 이동, 카테고리 탭과 겹치지 않게)
    if (this.countLabel) this.countLabel.destroy();
    this.countLabel = this.add.text(GAME_WIDTH - 320, 152, '', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '13px',
      fontStyle: '700',
      color: '#9b6bff',
    }).setOrigin(1, 0.5);
  }

  // ── Preview Panel ─────────────────────────────────────────────────────────

  private _buildPreviewPanel(): void {
    this.previewBg = this.add.graphics();
    this.previewBg.fillStyle(0xfff0fa, 1);
    this.previewBg.fillRoundedRect(GAME_WIDTH - 300, GRID_TOP, 280, VISIBLE_HEIGHT, 18);
    this.previewBg.lineStyle(2, 0xffd6e6, 1);
    this.previewBg.strokeRoundedRect(GAME_WIDTH - 300, GRID_TOP, 280, VISIBLE_HEIGHT, 18);

    this.add.text(PREVIEW_X, GRID_TOP + 30, '👗 착용 미리보기', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '14px',
      fontStyle: '800',
      color: '#9b6bff',
    }).setOrigin(0.5);

    this.add.text(PREVIEW_X, GRID_TOP + 55, '아이템 탭하면 입어본 모습', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '11px',
      color: '#b899cc',
    }).setOrigin(0.5);

    this.previewNameLabel = this.add.text(PREVIEW_X, GAME_HEIGHT - 130, '', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '12px',
      fontStyle: '700',
      color: '#e54e87',
      align: 'center',
      wordWrap: { width: 240 },
    }).setOrigin(0.5).setDepth(10);
  }

  private _showPreview(outfitId: string): void {
    const outfit = OUTFITS_BY_ID[outfitId];
    if (!outfit) return;

    try {
      const trialEquipped: Partial<Record<OutfitSlot, string>> = { ...GameState.data.currentEquipped, [outfit.slot]: outfitId };
      const hairId = trialEquipped.hair;
      const fullKey = hairId ? `char_full_${hairId}` : null;
      const useKey = fullKey && this.textures.exists(fullKey) ? fullKey : 'character_bald';

      // DOM 미리보기는 SVG 데이터 URL 모델이라 PNG 텍스처에 안 맞음 → Phaser Image로 갈아끼움
      if (this.previewDom) { this.previewDom.destroy(); this.previewDom = null; }
      if (this.previewSprite) this.previewSprite.destroy();
      if (this.textures.exists(useKey)) {
        this.previewSprite = this.add.image(PREVIEW_X, PREVIEW_Y, useKey).setScale(1.0).setDepth(9);
      }

      if (this.previewNameLabel) {
        this.previewNameLabel.setText(outfit.name).setDepth(10);
      }
    } catch (e) {
      console.warn('preview failed:', e);
    }
  }


  // ── Scroll ────────────────────────────────────────────────────────────────

  private _setupScrollInput(): void {
    this.input.on('wheel', (_ptr: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      this.targetScrollY = Phaser.Math.Clamp(this.targetScrollY + dy * 0.6, 0, Math.max(0, this.maxScrollY));
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.x < GAME_WIDTH - 310 && p.y > 145 && p.y < GAME_HEIGHT - 90) {
        this.isDragging = true;
        this.dragMoved = false;
        this.dragStartY = p.y;
        this.dragStartScroll = this.scrollY;
        this.dragLastY = p.y;
        this.dragLastTime = performance.now();
        this.dragVelocity = 0;
        this.targetScrollY = this.scrollY;
      }
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      const delta = this.dragStartY - p.y;
      if (Math.abs(delta) > 8) this.dragMoved = true;
      if (this.dragMoved) {
        const now = performance.now();
        const dt = Math.max(1, now - this.dragLastTime);
        this.dragVelocity = (this.dragLastY - p.y) / dt * 16;
        this.dragLastY = p.y;
        this.dragLastTime = now;
        const newY = Phaser.Math.Clamp(this.dragStartScroll + delta, 0, Math.max(0, this.maxScrollY));
        this.scrollY = newY;
        this.targetScrollY = newY;
        if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
        this._updateScrollbar();
      }
    });

    const endDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.dragMoved = false;
      this.targetScrollY = Phaser.Math.Clamp(this.scrollY + this.dragVelocity * 12, 0, Math.max(0, this.maxScrollY));
    };
    this.input.on('pointerup', endDrag);
    this.input.on('pointerupoutside', endDrag);
  }

  update(_time: number, delta: number): void {
    if (!this.isDragging && Math.abs(this.targetScrollY - this.scrollY) > 0.1) {
      const t = Math.min(1, (delta / 16) * 0.22);
      this.scrollY += (this.targetScrollY - this.scrollY) * t;
      if (this.gridContainer) this.gridContainer.y = GRID_TOP - this.scrollY;
      this._updateScrollbar();
    }
  }


  private _updateScrollbar(): void {
    if (!this.scrollbarThumb || !this.scrollbarTrack) return;
    if (this.maxScrollY <= 0) { this.scrollbarThumb.setVisible(false); return; }
    this.scrollbarThumb.setVisible(true);
    const trackH = this.scrollbarTrack.height;
    const thumbH = Math.max(40, (VISIBLE_HEIGHT / (VISIBLE_HEIGHT + this.maxScrollY)) * trackH);
    const thumbY = this.scrollbarTrack.y - trackH / 2 + (this.scrollY / this.maxScrollY) * (trackH - thumbH) + thumbH / 2;
    this.scrollbarThumb.setDisplaySize(8, thumbH);
    this.scrollbarThumb.setPosition(this.scrollbarTrack.x, thumbY);
  }

  // ── Shop Grid ─────────────────────────────────────────────────────────────

  private async renderShopGrid(): Promise<void> {
    if (this.gridContainer) this.gridContainer.destroy();
    if (this.scrollbarTrack) this.scrollbarTrack.destroy();
    if (this.scrollbarThumb) this.scrollbarThumb.destroy();
    this.cardElements.clear();

    this.gridContainer = this.add.container(0, GRID_TOP);

    // Mask for scroll clipping
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, GRID_TOP, GAME_WIDTH - 310, VISIBLE_HEIGHT);
    this.gridContainer.setMask(maskShape.createGeometryMask());

    const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
    // Show ALL shop items — mark owned ones with badge, not-owned with buy button
    let forSale = OUTFITS.filter(o => o.unlockMethod === 'shop' && o.cost);
    if (this.currentCategory !== 'all') {
      forSale = forSale.filter(o => o.slot === this.currentCategory);
    }
    // 정렬: 등급 높은 순 → 가격 낮은 순
    const RARITY_ORDER: Record<string, number> = { legend: 0, epic: 1, rare: 2, season: 0, common: 3 };
    forSale.sort((a, b) => {
      const r = (RARITY_ORDER[a.rarity] ?? 9) - (RARITY_ORDER[b.rarity] ?? 9);
      if (r !== 0) return r;
      const aCost = (a.cost?.coins ?? 0) + (a.cost?.stars ?? 0) * 10 + (a.cost?.gems ?? 0) * 100;
      const bCost = (b.cost?.coins ?? 0) + (b.cost?.stars ?? 0) * 10 + (b.cost?.gems ?? 0) * 100;
      return aCost - bCost;
    });

    if (this.countLabel) {
      const ownedHere = forSale.filter(o => ownedIds.has(o.id)).length;
      this.countLabel.setText(`${ownedHere} / ${forSale.length} 보유`);
    }

    const rows = Math.ceil(forSale.length / COLS);
    const contentHeight = rows * (CARD_H + 30) + 20;
    this.maxScrollY = Math.max(0, contentHeight - VISIBLE_HEIGHT);

    const promises: Promise<void>[] = [];

    forSale.forEach((it, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const x = START_X + col * COL_GAP;
      const y = 20 + row * (CARD_H + 30); // relative to container top
      const owned = ownedIds.has(it.id);
      const rarityCol = RARITY_COLOR[it.rarity] ?? 0xcccccc;

      // Drop shadow
      const shadow = this.add.graphics();
      shadow.fillStyle(0x3a2540, 0.1);
      shadow.fillRoundedRect(x - CARD_W / 2 + 3, y - CARD_H / 2 + 5, CARD_W, CARD_H, 18);

      // Card background
      const card = this.add.graphics();
      card.fillStyle(owned ? 0xf0fff0 : 0xffffff, 1);
      card.fillRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 18);
      card.lineStyle(2, rarityCol, owned ? 1 : 0.85);
      card.strokeRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 18);

      this.gridContainer!.add([shadow, card]);

      // OWNED badge (좌상단)
      if (owned) {
        const ownedBadge = this.add.graphics();
        ownedBadge.fillStyle(0x22cc66, 1);
        ownedBadge.fillRoundedRect(x - CARD_W / 2 + 4, y - CARD_H / 2 + 4, 64, 22, 8);
        const ownedText = this.add.text(x - CARD_W / 2 + 36, y - CARD_H / 2 + 15, '✓ 보유', {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '11px',
          fontStyle: '800',
          color: '#ffffff',
        }).setOrigin(0.5);
        this.gridContainer!.add([ownedBadge, ownedText]);
      }

      // RARITY 배지 (우상단)
      const rarityBadge = this.add.graphics();
      const rarityFillR = parseInt(RARITY_TEXT_COLOR[it.rarity].slice(1, 3), 16);
      const rarityFillG = parseInt(RARITY_TEXT_COLOR[it.rarity].slice(3, 5), 16);
      const rarityFillB = parseInt(RARITY_TEXT_COLOR[it.rarity].slice(5, 7), 16);
      const rarityHex = (rarityFillR << 16) | (rarityFillG << 8) | rarityFillB;
      rarityBadge.fillStyle(rarityHex, 0.15);
      rarityBadge.fillRoundedRect(x + CARD_W / 2 - 70, y - CARD_H / 2 + 4, 66, 22, 8);
      rarityBadge.lineStyle(1.5, rarityCol, 1);
      rarityBadge.strokeRoundedRect(x + CARD_W / 2 - 70, y - CARD_H / 2 + 4, 66, 22, 8);
      const rarityText = this.add.text(x + CARD_W / 2 - 37, y - CARD_H / 2 + 15, RARITY_LABEL[it.rarity] ?? '', {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '10px',
        fontStyle: '900',
        color: RARITY_TEXT_COLOR[it.rarity] ?? '#3a2540',
      }).setOrigin(0.5);
      this.gridContainer!.add([rarityBadge, rarityText]);

      // LEGEND/SEASON: 반짝이 효과 (오른쪽 상단 작은 별)
      if (it.rarity === 'legend' || it.rarity === 'season') {
        const sparkle = this.add.text(x + CARD_W / 2 - 12, y - CARD_H / 2 + 38, '✨', {
          fontSize: '14px',
        }).setOrigin(0.5);
        this.tweens.add({
          targets: sparkle,
          alpha: { from: 0.4, to: 1 },
          duration: 800,
          yoyo: true,
          repeat: -1,
        });
        this.gridContainer!.add(sparkle);
      }

      // Outfit icon
      const hairStyle = (it.slot === 'hair' && it.hairStyle) ? it.hairStyle as HairStyle : undefined;
      const iconKey = `shop_icon_${it.id}`;
      const svgStr = OutfitIconRenderer.render(it.slot, it.color, hairStyle, it.iconStyle);
      const p = svgStringToTexture(this, iconKey, svgStr).then(() => {
        if (!this.scene.isActive()) return;
        const icon = this.add.image(x, y - 40, iconKey);
        icon.setScale(3.2);
        if (owned) icon.setAlpha(0.7);
        this.gridContainer!.add(icon);
        // 카드 elements 추적 (구매 후 부분 업데이트용)
        const elem = this.cardElements.get(it.id);
        if (elem) elem.icon = icon;
      });
      promises.push(p);

      // 카드 elements 등록 (icon은 async, 다른 건 즉시)
      this.cardElements.set(it.id, { card, cardX: x, cardY: y });

      // 카테고리 작은 라벨
      const slotLabel = CATEGORIES.find(c => c.id === it.slot);
      if (slotLabel && this.currentCategory === 'all') {
        const slotTag = this.add.text(x, y + 22, `${slotLabel.emoji} ${slotLabel.label}`, {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '10px',
          fontStyle: '700',
          color: '#9b6bff',
        }).setOrigin(0.5);
        this.gridContainer!.add(slotTag);
      }

      // Name
      const name = this.add.text(x, y + 42, it.name, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '13px',
        fontStyle: '800',
        color: '#3a2540',
        align: 'center',
        wordWrap: { width: 160 },
      }).setOrigin(0.5);

      this.gridContainer!.add(name);

      if (!owned) {
        // Cost label
        const costStr = it.cost!.coins ? `💰 ${it.cost!.coins}`
                      : it.cost!.stars ? `⭐ ${it.cost!.stars}`
                      : `💎 ${it.cost!.gems}`;
        const cost = this.add.text(x, y + 65, costStr, {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '14px',
          fontStyle: '800',
          color: '#e54e87',
        }).setOrigin(0.5);
        this.gridContainer!.add(cost);

        // Buy button
        const buyBtn = new Button(this, x, y + 92, '구매', () => {
          const success = EconomyEngine.purchaseOutfit(it.id);
          if (success) {
            AudioManager.playSfx('purchase');
            this.toast.show(`✨ ${it.name} 구매 완료!`, 'gold');
            // 부분 업데이트: 해당 카드만 owned 상태로 (전체 재렌더 X — 렉 방지)
            this._markCardOwned(it.id);
            this._renderCurrencyChips();
          } else {
            this.toast.show(`❌ 잔액 부족`, 'error');
          }
        }, { width: 100, height: 34, style: 'primary' });
        if (buyBtn) this.gridContainer!.add(buyBtn);

        // 추적용 등록
        const elem = this.cardElements.get(it.id);
        if (elem) { elem.cost = cost; elem.buyBtn = buyBtn; }
      }

      // 탭 영역 (터치 환경 — pointerdown 사용)
      const hit = this.add.rectangle(x, y, CARD_W, CARD_H - 50, 0x000000, 0);  // 구매 버튼 영역 제외
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        AudioManager.playSfx('click');
        this._showPreview(it.id);
      });
      this.gridContainer!.add(hit);
    });

    // Scrollbar
    const sbX = GAME_WIDTH - 325;
    this.scrollbarTrack = this.add.rectangle(sbX, GRID_TOP + VISIBLE_HEIGHT / 2, 8, VISIBLE_HEIGHT, 0xddd0e6, 0.5);
    this.scrollbarThumb = this.add.rectangle(sbX, GRID_TOP + 20, 8, 40, 0xaa88cc);
    this._updateScrollbar();

    this.gridContainer.y = GRID_TOP - this.scrollY;

    await Promise.allSettled(promises);
  }

  /** 구매 후 해당 카드만 부분 업데이트 (전체 재렌더 X — 렉 방지) */
  private _markCardOwned(outfitId: string): void {
    const elem = this.cardElements.get(outfitId);
    if (!elem || !this.gridContainer) return;

    const { cardX: x, cardY: y, card, icon, cost, buyBtn } = elem;

    // 카드 배경 색 변경 (옅은 초록 — 보유 표시)
    if (card) {
      card.clear();
      const outfit = OUTFITS_BY_ID[outfitId];
      const rarityCol = outfit ? (RARITY_COLOR[outfit.rarity] ?? 0xcccccc) : 0xcccccc;
      card.fillStyle(0xf0fff0, 1);
      card.fillRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 18);
      card.lineStyle(2, rarityCol, 1);
      card.strokeRoundedRect(x - CARD_W / 2, y - CARD_H / 2, CARD_W, CARD_H, 18);
    }

    // 아이콘 흐리게
    if (icon) icon.setAlpha(0.7);

    // 비용/구매버튼 제거
    if (cost) cost.destroy();
    if (buyBtn) buyBtn.destroy();

    // OWNED 배지 추가
    const ownedBadge = this.add.graphics();
    ownedBadge.fillStyle(0x22cc66, 1);
    ownedBadge.fillRoundedRect(x - CARD_W / 2 + 4, y - CARD_H / 2 + 4, 64, 22, 8);
    const ownedText = this.add.text(x - CARD_W / 2 + 36, y - CARD_H / 2 + 15, '✓ 보유', {
      fontFamily: Theme.font.family, fontSize: '11px', fontStyle: '800', color: '#ffffff',
    }).setOrigin(0.5);
    this.gridContainer.add([ownedBadge, ownedText]);

    // 카운트 라벨 갱신
    if (this.countLabel) {
      const ownedIds = new Set(GameState.data.ownedOutfits.map(o => o.outfitId));
      let forSale = OUTFITS.filter(o => o.unlockMethod === 'shop' && o.cost);
      if (this.currentCategory !== 'all') forSale = forSale.filter(o => o.slot === this.currentCategory);
      const ownedHere = forSale.filter(o => ownedIds.has(o.id)).length;
      this.countLabel.setText(`${ownedHere} / ${forSale.length} 보유`);
    }
  }
}
